import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { usersApi } from '@/api/users-api'
import type { UpdateUserRequest } from '@/types/user'

export const useUsers = (nextToken?: string) => {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['users', { nextToken }],
    queryFn: async () => {
      try {
        return await usersApi.getUsers(nextToken)
      } catch (error) {
        console.error('Failed to fetch users:', error)
        throw error
      }
    }
  })

  const createUser = useMutation({
    mutationFn: async (userId: string) => {
      try {
        await usersApi.createUser(userId)
      } catch (error) {
        console.error('Failed to create user:', error)
        throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    }
  })

  const updateUser = useMutation({
    mutationFn: async (request: UpdateUserRequest) => {
      try {
        await usersApi.updateUser(request)
      } catch (error) {
        console.error('Failed to update user:', error)
        throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    }
  })

  const deleteUser = useMutation({
    mutationFn: async (userId: string) => {
      try {
        await usersApi.deleteUser(userId)
      } catch (error) {
        console.error('Failed to delete user:', error)
        throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    }
  })

  if (query.isPending) {
    return {
      users: [],
      nextToken: undefined,
      isLoading: true,
      error: null,
      createUser,
      updateUser,
      deleteUser
    }
  }

  if (query.isError) {
    return {
      users: [],
      nextToken: undefined,
      isLoading: false,
      error: 'Failed to load users.',
      createUser,
      updateUser,
      deleteUser
    }
  }

  return {
    users: query.data?.items ?? [],
    nextToken: query.data?.next_token,
    isLoading: false,
    error: null,
    createUser,
    updateUser,
    deleteUser
  }
}